import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useForm, FormProvider } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';

import { PassengerDetailsForm } from './PassengerDetailsForm';
import { Spinner } from 'admin-components/components';
import { historyShape, userShape } from 'constants/propTypes';
import { GET_TRAVEL_PASS_PRODUCT_OFFERS } from 'store/queries';
import TextInput from 'components/ui/TextInput';
import { ConfirmButton } from 'components/ui/Button';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import config from 'utils/config';
import countries from 'constants/countries';
import { NO_VAT_TTP } from 'constants/clubEurostar';
import { TYPE_OF_USE } from '../../types/constants';
import { areThalysProductsConformWithRequest } from './BuyThalysPass.utils';
import NotLoggedInRedirectButton from './NotLoggedInRedirectButton';
import {
  StyledHeading,
  StyledPage,
  StyledLabel,
  StyledH2,
  FormContainer,
  FormContainerDate,
  ErrorMessage,
  StyledInputFields,
  StyledPassengerDetails,
  StyledDateField,
  ThalysPassDetailsContainer,
  StyledRadioGroup,
  StyledRadio,
  StyledCalendar,
  StyledCalendarIcon,
  StyledButton,
  StyledForm,
  StyledSelect,
  StyledRowRadioGroup,
  RadioButtonsContainer,
  RowContainer,
  ColumnContainer
} from './BuyThalysPass.styles';
import VatValidationForm from './VatValidationForm';
import { Column } from '../../components/shared/Layout';
import { CONFIGURE_SUBSCRIPTION_RENEWAL_OVERLAP } from 'constants/launchDarkly';
import { useFlags } from 'launchdarkly-react-client-sdk';

export const vatSubjectStatus = {
  NO: 'NO',
  EU: 'EU',
  NON_EU: 'NON_EU'
};

const validateFormInputMaxLength = {
  companyName: 50,
  street: 100,
  city: 50,
  postcode: 15
};

const BE_VAT_COUNTRY = 'BE';
const defaultValues = {
  validityDate: '',
  typeOfUse: TYPE_OF_USE.BUSINESS,
  invoicingAddress: 'false',
  vatSubject: vatSubjectStatus.NO,
  vatValid: false,
  vatValidNumber: '',
  vatCountry: 'GB',
  vatNumber: '',
  passengerFirstName: '',
  passengerLastName: '',
  passengerCompany: '',
  passengerEmail: '',
  passengerPhoneNumber: '',
  passengerPhoneCountryCode: '',
  passengerDateOfBirth: '',
  passengerStreet: '',
  passengerCity: '',
  passengerPostcode: '',
  passengerCountry: 'GB',
  invoicingCompany: '',
  invoicingStreet: '',
  invoicingCity: '',
  invoicingPostcode: '',
  invoicingCountry: 'GB'
};

const BuyThalysPass = ({ user, history, onSubmit, createProvisionalBookingLoading, createProvisionalBookingError }) => {
  const isThalysPassEnabled = config('ENABLE_THALYS_PASS', { canOverride: true }) === 'true';
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const startDateStr = new URLSearchParams(history.location.search).get('startDate');
  const membershipNumberUrl = membershipNumber ? `?membershipNumber=${membershipNumber}` : '';
  const shouldShowLogInWarning = !membershipNumber;

  const configureSubscriptionRenewalOverlap = useFlags()[CONFIGURE_SUBSCRIPTION_RENEWAL_OVERLAP];

  const [shouldRefetchThalysAvailabilities, setShouldRefetchThalysAvailabilities] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [vatCountryMismatch, setVatCountryMismatch] = useState(false);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });

  const { register, handleSubmit, formState, setValue, watch } = formMethods;
  const { errors } = formState;

  const {
    validityDate,
    typeOfUse,
    invoicingAddress,
    vatValid,
    vatSubject,
    vatValidNumber,
    thalysProduct,
    vatCountry,
    passengerCountry,
    invoicingCountry
  } = watch();

  const formValidityDate = validityDate;
  const formTypeOfUse = typeOfUse;
  const formInvoicingAddress = invoicingAddress === 'true';
  const formVatValid = vatValid;
  const formVatEUSubject = vatSubject === vatSubjectStatus.EU;
  const formVatNonEUSubject = vatSubject === vatSubjectStatus.NON_EU;
  const formVatValidNumber = vatValidNumber;
  const formThalysProduct = thalysProduct;

  const submit = (values) => {
    if (vatSubject !== 'NO') {
      const match = invoicingAddress === 'true' ? passengerCountry === vatCountry : invoicingCountry === vatCountry;

      if (!match) {
        setVatCountryMismatch(true);
        return;
      }
    }

    setVatCountryMismatch(false);
    onSubmit(values);
  };

  const [getTravelPassProductOffers, { loading: getTravelPassProductOffersLoading, data: getTravelPassProductOffersData }] =
    useLazyQuery(GET_TRAVEL_PASS_PRODUCT_OFFERS);

  const tomorrowOrStartDateOkay = startDateStr ? moment.unix(startDateStr).toDate() : moment().add(1, 'days').toDate();

  const aMonthFromNow = new Date(
    tomorrowOrStartDateOkay.getFullYear(),
    tomorrowOrStartDateOkay.getMonth(),
    tomorrowOrStartDateOkay.getDate() + configureSubscriptionRenewalOverlap
  );

  if (!user?.canSellTravelPass || !isThalysPassEnabled) {
    history.push('/');
  }

  const searchThalysPassAvailabilities = async (thalysPassDate) => {
    setShouldRefetchThalysAvailabilities(false);

    const formattedThalysPassDate = moment(thalysPassDate, 'DD-MM-YYYY').format('YYYY-MM-DD');

    getTravelPassProductOffers({
      variables: {
        departureDate: formattedThalysPassDate
      }
    });
  };

  const handleCalendarChange = (event) => {
    setValue('validityDate', moment(event).format('DD-MM-YYYY'), {
      shouldValidate: true
    });
    setShouldRefetchThalysAvailabilities(true);
    setCalendarOpen(false);
  };

  if (createProvisionalBookingError) {
    throw new AgentPortalError(createProvisionalBookingError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/buy-thalys-pass${membershipNumberUrl}`
    });
  }

  const travelPassProducts = getTravelPassProductOffersData?.getTravelPassProductOffers ?? [];
  const groupedTravelPassProducts = travelPassProducts.reduce(
    (groupedProducts, product) => {
      if (product?.tariffCode?.includes(NO_VAT_TTP)) {
        groupedProducts.withoutVat.push(product);
        return groupedProducts;
      }

      groupedProducts.withVat.push(product);
      return groupedProducts;
    },
    {
      withVat: [],
      withoutVat: []
    }
  );

  const shouldSelectSubscriptionWithoutVat =
    (!shouldRefetchThalysAvailabilities &&
      formVatEUSubject &&
      formVatValid &&
      !formThalysProduct?.includes(NO_VAT_TTP) &&
      BE_VAT_COUNTRY !== formVatValidNumber?.slice(0, 2)) ||
    (!shouldRefetchThalysAvailabilities && formVatNonEUSubject && !formThalysProduct?.includes(NO_VAT_TTP));
  const shouldSelectSubscriptionWithVat =
    (!shouldRefetchThalysAvailabilities &&
      formVatEUSubject &&
      formVatValid &&
      formThalysProduct?.includes(NO_VAT_TTP) &&
      BE_VAT_COUNTRY === formVatValidNumber?.slice(0, 2)) ||
    (!shouldRefetchThalysAvailabilities && !formVatEUSubject && !formVatNonEUSubject && formThalysProduct?.includes(NO_VAT_TTP));

  if (shouldShowLogInWarning) {
    return (
      <StyledPage>
        <NotLoggedInRedirectButton history={history} />;
      </StyledPage>
    );
  }

  return (
    <StyledPage>
      <FormProvider {...formMethods}>
        <StyledHeading>
          <FormattedMessage id="homepage.thalyspass" />
        </StyledHeading>
        <StyledForm data-testid="buy-thalys-pass-form" onSubmit={handleSubmit(submit)}>
          <ThalysPassDetailsContainer>
            <StyledPassengerDetails>
              <StyledH2>
                <FormattedMessage id="vouchers.travelPeriodStart" />
              </StyledH2>
            </StyledPassengerDetails>
            <FormContainerDate>
              <StyledDateField htmlFor="validityDate" onClick={() => setCalendarOpen(!calendarOpen)}>
                <TextInput
                  type="text"
                  readOnly
                  id="validityDate"
                  value={formValidityDate}
                  placeholder="DD-MM-YYYY"
                  {...register('validityDate', { required: true })}
                />
                <StyledCalendarIcon size={20} />
              </StyledDateField>
              <StyledButton
                type="button"
                onClick={() => {
                  searchThalysPassAvailabilities(formValidityDate);
                }}
                data-cy="search-thalys-pass-products"
                disabled={!formValidityDate || getTravelPassProductOffersLoading || createProvisionalBookingLoading}
                loading={getTravelPassProductOffersLoading}
              >
                <FormattedMessage id="thalyspass.getAvailabilities" />
              </StyledButton>
            </FormContainerDate>
            {calendarOpen && (
              <StyledCalendar
                showNeighboringMonth={false}
                tabIndex="-1"
                value={tomorrowOrStartDateOkay}
                onChange={handleCalendarChange}
                minDate={tomorrowOrStartDateOkay}
                maxDate={aMonthFromNow}
              />
            )}
          </ThalysPassDetailsContainer>
          <ThalysPassDetailsContainer>
            <StyledPassengerDetails>
              <StyledH2>
                <FormattedMessage id="thalyspass.subscription" />
              </StyledH2>
            </StyledPassengerDetails>
            <FormContainer>
              {getTravelPassProductOffersLoading ? (
                <Column alignItems="center" justifyContent="center">
                  <Spinner />
                </Column>
              ) : (
                <>
                  {shouldRefetchThalysAvailabilities && <FormattedMessage id="thalyspass.checkAvailabilities" />}
                  {!shouldRefetchThalysAvailabilities && travelPassProducts?.length === 0 && (
                    <ErrorMessage>
                      <FormattedMessage id="thalyspass.noAvailabilities" />
                    </ErrorMessage>
                  )}
                  {!shouldRefetchThalysAvailabilities && areThalysProductsConformWithRequest(travelPassProducts, formValidityDate) && (
                    <StyledRadioGroup data-cy="radio-group-thalys">
                      <RowContainer>
                        {groupedTravelPassProducts?.withVat?.length > 0 && (
                          <ColumnContainer>
                            <StyledH2>
                              <FormattedMessage id="thalyspass.withVAT" />
                            </StyledH2>
                            {groupedTravelPassProducts.withVat.map((tp, index) => (
                              <div key={`${tp.tariffCode}-${tp.price}`} style={{ marginBottom: '5px' }}>
                                <label htmlFor={`${tp.tariffCode}-${tp.price}`}>
                                  <StyledRadio
                                    id="thalysProduct"
                                    type="radio"
                                    value={tp.tariffCodeActual}
                                    defaultChecked={index === 0}
                                    {...register('thalysProduct')}
                                  />
                                  {tp.productName} - <FormattedCurrency currency="EUR" amount={tp.price} />
                                </label>
                              </div>
                            ))}
                          </ColumnContainer>
                        )}
                        {groupedTravelPassProducts?.withoutVat?.length > 0 && (
                          <ColumnContainer>
                            <StyledH2>
                              <FormattedMessage id="thalyspass.withoutVAT" />
                            </StyledH2>
                            {groupedTravelPassProducts.withoutVat.map((tp) => (
                              <div key={`${tp.tariffCode}-${tp.price}`} style={{ marginBottom: '5px' }}>
                                <label htmlFor={`${tp.tariffCode}-${tp.price}`}>
                                  <StyledRadio type="radio" id="thalysProduct" value={tp.tariffCodeActual} {...register('thalysProduct')} />
                                  {tp.productName} - <FormattedCurrency currency="EUR" amount={tp.price} />
                                </label>
                              </div>
                            ))}
                          </ColumnContainer>
                        )}
                      </RowContainer>
                    </StyledRadioGroup>
                  )}
                  {shouldSelectSubscriptionWithoutVat && (
                    <ErrorMessage style={{ marginTop: 8 }}>
                      <FormattedMessage id="thalyspass.selectPassWithoutVat" />
                    </ErrorMessage>
                  )}
                  {shouldSelectSubscriptionWithVat && (
                    <ErrorMessage style={{ marginTop: 8 }}>
                      <FormattedMessage id="thalyspass.selectPassWithVat" />
                    </ErrorMessage>
                  )}
                </>
              )}
            </FormContainer>
          </ThalysPassDetailsContainer>
          <ThalysPassDetailsContainer>
            <StyledPassengerDetails>
              <StyledH2>
                <FormattedMessage id="extrasOnlyBookings.passengerDetails" />
              </StyledH2>
            </StyledPassengerDetails>
            <PassengerDetailsForm membershipNumber={membershipNumber} />
          </ThalysPassDetailsContainer>
          <ThalysPassDetailsContainer>
            <StyledPassengerDetails>
              <StyledH2>
                <FormattedMessage id="thalyspass.invoicing" />
              </StyledH2>
            </StyledPassengerDetails>
            <FormContainer>
              <RadioButtonsContainer style={{ marginBottom: '16px' }}>
                <StyledLabel htmlFor="passengerEmail">
                  <FormattedMessage id="thalyspass.typeOfUse" />
                </StyledLabel>
                <StyledRowRadioGroup>
                  <label htmlFor="typeOfUseBusiness">
                    <StyledRadio
                      type="radio"
                      id="typeOfUseBusiness"
                      value={TYPE_OF_USE.BUSINESS}
                      {...register('typeOfUse', { required: true })}
                    />
                    <FormattedMessage id="thalyspass.business" />
                  </label>
                  <label htmlFor="typeOfUsePersonal">
                    <StyledRadio
                      type="radio"
                      id="typeOfUsePersonal"
                      value={TYPE_OF_USE.PERSONAL}
                      {...register('typeOfUse', { required: true })}
                    />
                    <FormattedMessage id="thalyspass.personal" />
                  </label>
                </StyledRowRadioGroup>
              </RadioButtonsContainer>
              <RowContainer>
                <ColumnContainer>
                  <StyledH2>
                    <FormattedMessage id="billingDetailsForm.address" />
                  </StyledH2>
                  {TYPE_OF_USE.BUSINESS === formTypeOfUse ? (
                    <StyledInputFields>
                      <StyledLabel mandatory htmlFor="passengerCompany">
                        <FormattedMessage id="baf.companyName" />
                      </StyledLabel>
                      <TextInput
                        id="passengerCompany"
                        type="text"
                        {...register('passengerCompany', {
                          required: true,
                          maxLength: { value: validateFormInputMaxLength.companyName, message: 'maxLengthValidationError' }
                        })}
                        maxLength={validateFormInputMaxLength.companyName + 1}
                      />
                      {errors.passengerCompany && (
                        <ErrorMessage>
                          <FormattedMessage
                            id={
                              errors.passengerCompany.message === 'maxLengthValidationError'
                                ? 'subscription.companyNameValidationLength'
                                : 'billingDetailsForm.incompleteField'
                            }
                          />
                        </ErrorMessage>
                      )}
                    </StyledInputFields>
                  ) : null}
                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="passengerStreet">
                      <FormattedMessage id="billingDetailsForm.street" />
                    </StyledLabel>
                    <TextInput
                      id="passengerStreet"
                      type="text"
                      {...register('passengerStreet', {
                        required: true,
                        maxLength: { value: validateFormInputMaxLength.street, message: 'maxLengthValidationError' }
                      })}
                      maxLength={validateFormInputMaxLength.street + 1}
                    />
                    {errors.passengerStreet && (
                      <ErrorMessage>
                        <FormattedMessage
                          id={
                            errors.passengerStreet.message === 'maxLengthValidationError'
                              ? 'subscription.streetValidationLength'
                              : 'billingDetailsForm.incompleteField'
                          }
                        />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>
                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="passengerCity">
                      <FormattedMessage id="billingDetailsForm.city" />
                    </StyledLabel>
                    <TextInput
                      id="passengerCity"
                      type="text"
                      {...register('passengerCity', {
                        required: true,
                        maxLength: { value: validateFormInputMaxLength.city, message: 'maxLengthValidationError' }
                      })}
                      maxLength={validateFormInputMaxLength.city + 1}
                    />
                    {errors.passengerCity && (
                      <ErrorMessage>
                        <FormattedMessage
                          id={
                            errors.passengerCity.message === 'maxLengthValidationError'
                              ? 'subscription.cityOrTownValidationLength'
                              : 'billingDetailsForm.incompleteField'
                          }
                        />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>
                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="passengerPostcode">
                      <FormattedMessage id="billingDetailsForm.postcode" />
                    </StyledLabel>
                    <TextInput
                      id="passengerPostcode"
                      type="text"
                      {...register('passengerPostcode', {
                        required: true,
                        maxLength: { value: validateFormInputMaxLength.postcode, message: 'maxLengthValidationError' }
                      })}
                      maxLength={validateFormInputMaxLength.postcode + 1}
                    />
                    {errors.passengerPostcode && (
                      <ErrorMessage>
                        <FormattedMessage
                          id={
                            errors.passengerPostcode.message === 'maxLengthValidationError'
                              ? 'subscription.postCodeValidationLength'
                              : 'billingDetailsForm.incompleteField'
                          }
                        />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>
                  <StyledInputFields>
                    <StyledLabel mandatory htmlFor="passengerCountry">
                      <FormattedMessage id="billingDetailsForm.country" />
                    </StyledLabel>
                    <StyledSelect
                      {...register('passengerCountry', {
                        required: true
                      })}
                      id="passengerCountry"
                    >
                      {countries.map(({ code, name }) => (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      ))}
                    </StyledSelect>
                    {errors.passengerCountry && (
                      <ErrorMessage>
                        <FormattedMessage id="thalyspass.errors.vatMismatch" />
                      </ErrorMessage>
                    )}
                  </StyledInputFields>
                </ColumnContainer>
                <ColumnContainer>
                  <RadioButtonsContainer>
                    <StyledH2>
                      <FormattedMessage id="thalyspass.invoicingAddressTitle" />
                    </StyledH2>
                    <StyledLabel htmlFor="invoicingAddress">
                      (<FormattedMessage id="thalyspass.invoicingAddressDescription" />
                    </StyledLabel>
                    <StyledRowRadioGroup>
                      <label htmlFor="invoicingAddressNo">
                        <StyledRadio
                          type="radio"
                          id="invoicingAddressNo"
                          value={'false'}
                          {...register('invoicingAddress', { required: true })}
                        />
                        <FormattedMessage id="interaction.no" />
                      </label>
                      <label htmlFor="invoicingAddressYes">
                        <StyledRadio
                          type="radio"
                          id="invoicingAddressYes"
                          value={'true'}
                          {...register('invoicingAddress', { required: true })}
                        />
                        <FormattedMessage id="interaction.yes" />)
                      </label>
                    </StyledRowRadioGroup>
                  </RadioButtonsContainer>
                  {!formInvoicingAddress ? (
                    <>
                      {TYPE_OF_USE.BUSINESS === formTypeOfUse ? (
                        <StyledInputFields>
                          <StyledLabel mandatory htmlFor="invoicingCompany">
                            <FormattedMessage id="baf.companyName" />
                          </StyledLabel>
                          <TextInput
                            id="invoicingCompany"
                            type="text"
                            {...register('invoicingCompany', {
                              required: true,
                              maxLength: { value: validateFormInputMaxLength.companyName, message: 'maxLengthValidationError' }
                            })}
                            maxLength={validateFormInputMaxLength.companyName + 1}
                          />
                          {errors.invoicingCompany && (
                            <ErrorMessage>
                              <FormattedMessage
                                id={
                                  errors.invoicingCompany.message === 'maxLengthValidationError'
                                    ? 'subscription.companyNameValidationLength'
                                    : 'billingDetailsForm.incompleteField'
                                }
                              />
                            </ErrorMessage>
                          )}
                        </StyledInputFields>
                      ) : null}
                      <StyledInputFields>
                        <StyledLabel mandatory htmlFor="invoicingStreet">
                          <FormattedMessage id="billingDetailsForm.street" />
                        </StyledLabel>
                        <TextInput
                          id="invoicingStreet"
                          type="text"
                          {...register('invoicingStreet', {
                            required: true,
                            maxLength: { value: validateFormInputMaxLength.street, message: 'maxLengthValidationError' }
                          })}
                          maxLength={validateFormInputMaxLength.street + 1}
                        />
                        {errors.invoicingStreet && (
                          <ErrorMessage>
                            <FormattedMessage
                              id={
                                errors.invoicingStreet.message === 'maxLengthValidationError'
                                  ? 'subscription.streetValidationLength'
                                  : 'billingDetailsForm.incompleteField'
                              }
                            />
                          </ErrorMessage>
                        )}
                      </StyledInputFields>
                      <StyledInputFields>
                        <StyledLabel mandatory htmlFor="invoicingCity">
                          <FormattedMessage id="billingDetailsForm.city" />
                        </StyledLabel>
                        <TextInput
                          id="invoicingCity"
                          type="text"
                          {...register('invoicingCity', {
                            required: true,
                            maxLength: { value: validateFormInputMaxLength.city, message: 'maxLengthValidationError' }
                          })}
                          maxLength={validateFormInputMaxLength.city + 1}
                        />
                        {errors.invoicingCity && (
                          <ErrorMessage>
                            <FormattedMessage
                              id={
                                errors.invoicingCity.message === 'maxLengthValidationError'
                                  ? 'subscription.cityOrTownValidationLength'
                                  : 'billingDetailsForm.incompleteField'
                              }
                            />
                          </ErrorMessage>
                        )}
                      </StyledInputFields>
                      <StyledInputFields>
                        <StyledLabel mandatory htmlFor="invoicingPostcode">
                          <FormattedMessage id="billingDetailsForm.postcode" />
                        </StyledLabel>
                        <TextInput
                          id="invoicingPostcode"
                          type="text"
                          {...register('invoicingPostcode', {
                            required: true,
                            maxLength: { value: validateFormInputMaxLength.postcode, message: 'maxLengthValidationError' }
                          })}
                          maxLength={validateFormInputMaxLength.postcode + 1}
                        />
                        {errors.invoicingPostcode && (
                          <ErrorMessage>
                            <FormattedMessage
                              id={
                                errors.invoicingPostcode.message === 'maxLengthValidationError'
                                  ? 'subscription.postCodeValidationLength'
                                  : 'billingDetailsForm.incompleteField'
                              }
                            />
                          </ErrorMessage>
                        )}
                      </StyledInputFields>
                      <StyledInputFields>
                        <StyledLabel mandatory htmlFor="invoicingCountry">
                          <FormattedMessage id="billingDetailsForm.country" />
                        </StyledLabel>
                        <StyledSelect
                          {...register('invoicingCountry', {
                            required: true
                          })}
                          id="invoicingCountry"
                        >
                          {countries.map(({ code, name }) => (
                            <option key={code} value={code}>
                              {name}
                            </option>
                          ))}
                        </StyledSelect>
                        {errors.invoicingCountry && (
                          <ErrorMessage>
                            <FormattedMessage id={errors.invoicingCountry.message === 'vatMismatch' && 'thalyspass.errors.vatMatch'} />
                          </ErrorMessage>
                        )}
                      </StyledInputFields>
                    </>
                  ) : null}
                </ColumnContainer>
              </RowContainer>
            </FormContainer>
          </ThalysPassDetailsContainer>
          {TYPE_OF_USE.BUSINESS === formTypeOfUse ? <VatValidationForm vatCountryMismatch={vatCountryMismatch} /> : null}
          <ConfirmButton
            type="submit"
            data-cy="buy-thalys-pass-form-submit"
            style={{ width: '150px' }}
            loading={createProvisionalBookingLoading}
            disabled={
              !formMethods.formState.isValid ||
              shouldShowLogInWarning ||
              getTravelPassProductOffersLoading ||
              createProvisionalBookingLoading ||
              shouldRefetchThalysAvailabilities ||
              shouldSelectSubscriptionWithoutVat ||
              shouldSelectSubscriptionWithVat ||
              !areThalysProductsConformWithRequest(travelPassProducts, formValidityDate)
            }
          >
            <FormattedMessage id="thalyspass.purchase" />
          </ConfirmButton>
        </StyledForm>
      </FormProvider>
    </StyledPage>
  );
};

BuyThalysPass.propTypes = {
  user: userShape,
  history: historyShape,
  onSubmit: PropTypes.func,
  createProvisionalBookingLoading: PropTypes.bool,
  createProvisionalBookingError: PropTypes.bool
};

export default BuyThalysPass;
